import React from 'react';

interface Props {
  status: string;
  priority: string;
  taskType: string;
  dueDateFrom: string;
  dueDateTo: string;
  overdue: boolean;
  onStatusChange: (v: string) => void;
  onPriorityChange: (v: string) => void;
  onTaskTypeChange: (v: string) => void;
  onDueDateFromChange: (v: string) => void;
  onDueDateToChange: (v: string) => void;
  onOverdueChange: (v: boolean) => void;
}

const idleCls = 'flex-shrink-0 flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[11px] font-medium transition-colors border-[#EDEDED] bg-ui-subtle text-ui-text3 hover:border-brand-dark/30 hover:text-ui-text hover:bg-ui-hover';
const activeCls = (color: string) => `flex-shrink-0 flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ring-1 ring-offset-1 ring-current/20 ${color}`;
const allActiveCls = 'bg-brand-dark border-brand-dark text-white';

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5 flex-shrink-0">
    <polyline points="2,6 5,9 10,3" />
  </svg>
);

const Chip: React.FC<{
  label: string;
  isActive: boolean;
  activeColor?: string;
  dot?: string;
  onClick: () => void;
}> = ({ label, isActive, activeColor, dot, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={isActive ? activeCls(activeColor ?? allActiveCls) : idleCls}
  >
    {isActive && <CheckIcon />}
    {!isActive && dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />}
    {label}
  </button>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-3 py-2 border-b border-[#EDEDED]/50 last:border-0">
    <span className="text-[10px] font-semibold text-ui-text3 uppercase tracking-wider w-14 flex-shrink-0 pt-0.5">
      {label}
    </span>
    <div className="flex flex-wrap gap-1">{children}</div>
  </div>
);

const dateInputCls = 'px-1.5 py-0.5 rounded-lg border border-[#EDEDED] bg-ui-subtle text-ui-text focus:outline-none focus:border-brand-dark/40 transition-colors';

const TaskListFilters: React.FC<Props> = ({
  status, priority, taskType, dueDateFrom, dueDateTo, overdue,
  onStatusChange, onPriorityChange, onTaskTypeChange,
  onDueDateFromChange, onDueDateToChange, onOverdueChange,
}) => {
  const today = new Date().toISOString().split('T')[0];

  const handleOverdue = () => {
    if (overdue) {
      onOverdueChange(false);
      onDueDateFromChange('');
      onDueDateToChange('');
    } else {
      onOverdueChange(true);
      onDueDateFromChange('');
      onDueDateToChange(today);
    }
  };

  const handleDateChange = (field: 'from' | 'to', val: string) => {
    if (overdue) onOverdueChange(false);
    if (field === 'from') onDueDateFromChange(val);
    else onDueDateToChange(val);
  };

  return (
    <div className="divide-y-0">
      {/* Status */}
      <Row label="Status">
        <Chip label="All"       isActive={status === ''}          onClick={() => onStatusChange('')} />
        <Chip label="Open"      isActive={status === 'open'}      activeColor="bg-blue-50 border-blue-300 text-blue-700"   onClick={() => onStatusChange('open')} />
        <Chip label="Done"      isActive={status === 'completed'} activeColor="bg-green-50 border-green-300 text-green-700" onClick={() => onStatusChange('completed')} />
        <Chip label="Cancelled" isActive={status === 'cancelled'} activeColor="bg-gray-100 border-gray-400 text-gray-600"  onClick={() => onStatusChange('cancelled')} />
      </Row>

      {/* Priority */}
      <Row label="Priority">
        <Chip label="All"    isActive={priority === ''}       onClick={() => onPriorityChange('')} />
        <Chip label="Low"    isActive={priority === 'low'}    dot="bg-gray-400"  activeColor="bg-gray-50 border-gray-400 text-gray-600"    onClick={() => onPriorityChange('low')} />
        <Chip label="Normal" isActive={priority === 'normal'} dot="bg-blue-400"  activeColor="bg-blue-50 border-blue-300 text-blue-700"    onClick={() => onPriorityChange('normal')} />
        <Chip label="High"   isActive={priority === 'high'}   dot="bg-amber-400" activeColor="bg-amber-50 border-amber-400 text-amber-700" onClick={() => onPriorityChange('high')} />
        <Chip label="Urgent" isActive={priority === 'urgent'} dot="bg-red-500"   activeColor="bg-red-50 border-red-400 text-red-700"       onClick={() => onPriorityChange('urgent')} />
      </Row>

      {/* Type */}
      <Row label="Type">
        <Chip label="All"       isActive={taskType === ''}          onClick={() => onTaskTypeChange('')} />
        <Chip label="General"   isActive={taskType === 'general'}   onClick={() => onTaskTypeChange('general')} />
        <Chip label="Follow up" isActive={taskType === 'follow_up'} onClick={() => onTaskTypeChange('follow_up')} />
        <Chip label="Approval"  isActive={taskType === 'approval'}  onClick={() => onTaskTypeChange('approval')} />
        <Chip label="Review"    isActive={taskType === 'review'}    onClick={() => onTaskTypeChange('review')} />
      </Row>

      {/* Due date */}
      <Row label="Due">
        <Chip
          label="Overdue"
          isActive={overdue}
          activeColor="bg-red-50 border-red-400 text-red-700"
          onClick={handleOverdue}
        />
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 ml-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-ui-text3">From</span>
            <input
              type="date"
              value={dueDateFrom}
              disabled={overdue}
              onChange={(e) => handleDateChange('from', e.target.value)}
              className={`${dateInputCls} disabled:opacity-40`}
              style={{ fontSize: '9px', width: '80px', transform: 'scale(0.85)', transformOrigin: 'left center' }}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-ui-text3">to</span>
            <input
              type="date"
              value={dueDateTo}
              disabled={overdue}
              onChange={(e) => handleDateChange('to', e.target.value)}
              className={`${dateInputCls} disabled:opacity-40`}
              style={{ fontSize: '9px', width: '80px', transform: 'scale(0.85)', transformOrigin: 'left center' }}
            />
          </div>
        </div>
      </Row>
    </div>
  );
};

export default TaskListFilters;
